// src/routes/core/createProtectedRoute.tsx
import React from 'react';
import { SecureQuantumPage } from '@/security/SecureQuantumPage';
import { withRouteAnalytics } from './withRouteAnalytics';
import type { AppRoute } from './types';

interface CreateRouteOptions {
  tier?: AppRoute['tier'];
  protected?: boolean;
  meta?: AppRoute['meta'];
}

export function createProtectedRoute(
  element: React.ReactNode,
  options?: CreateRouteOptions,
): React.ReactNode {
  const { tier, protected: isProtected = true, meta } = options || {};

  let wrapped = element;

  if (isProtected) {
    // Pass tier through to SecureQuantumPage. It will only enforce it
    // when ENABLE_TIER_GATING=true inside SecureQuantumPage — no changes
    // needed here when toggling the flag.
    wrapped = (
      <SecureQuantumPage
        requireTier={tier}
        multiverseAccess={meta?.category === 'multiverse'}
      >
        {wrapped}
      </SecureQuantumPage>
    );
  } else {
    // Public route — bypass auth entirely
    wrapped = (
      <SecureQuantumPage skipAuth={true}>
        {wrapped}
      </SecureQuantumPage>
    );
  }

  if (meta?.title) {
    // meta.toolId is opt-in (see RouteMeta in ./types.ts) — only routes that
    // set it get a tool_usage_events row on mount. Everything else keeps the
    // exact same console-log-only behavior as before.
    const AnalyticsWrapper = withRouteAnalytics(
      wrapped,
      meta.title,
      meta.toolId
        ? { toolId: meta.toolId, toolName: meta.title, country: meta.region, category: meta.category }
        : undefined,
    );
    wrapped = <AnalyticsWrapper />;
  }

  return wrapped;
}